/**
 * The queue's default view, kept in the URL.
 *
 * Sales who open the queue see: the "To review" tab, cases that are theirs or nobody's,
 * received today (the viewer's calendar day; decision of 12 Oct 2026, before that the
 * last 3 working days), in the mailboxes of their scope, with the inquiries a colleague
 * forwarded and with the replies that bring no new lines folded under their first mail.
 * Older open inquiries are one click away under the backlog chips. Nothing of that is
 * hidden state: on a fresh visit the defaults are written into the search params
 * (`assignee`, `days=0`, `mailbox`), so sales can widen any of them and share the link.
 * Two toggles turn the new defaults off for the viewer: `internal=0` hides colleagues'
 * mails and `threads=all` shows every reply as its own row.
 *
 * A visit is fresh when the URL has no `assignee` param. Once it is there (also as
 * `any` for "Everyone") the URL is taken as the user's own choice and nothing is added.
 *
 * The last mailbox choice is remembered per browser for users with more than one
 * mailbox. Storage can be unavailable (private mode, blocked): every access is guarded.
 *
 * Built-in views ("Today", "This week", "Older than 7 days") are presets over the same
 * URL: a view sets only the received dates and the "To review" tab. It keeps the user's
 * assignee, mailboxes and areas, so a view never widens what a user sees. The status
 * views (Ready to quote, Customer to confirm, ...) are the tabs: a tab keeps the dates.
 */

import type {
  QueueAssignee,
  QueueAttention,
  QueueClosedFilter,
  QueueFilters,
  QueueView,
} from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { intentOptionsFor } from "@/lib/intents";
import { SENDER_OPTIONS } from "@/lib/work-labels";

export const DEFAULT_ASSIGNEE: QueueAssignee = "mine_or_unassigned";
/** The tab with everything except the parked purchase orders (docs/scope.md). */
export const ALL_TAB_LABEL = "All except purchase orders";
/**
 * The `days` of the default view: 0 days back, so today only. Kept as a relative value
 * (not `from`/`to` of a fixed date) so a remembered or shared default view still means
 * "today" tomorrow.
 */
export const TODAY_DAYS = 0;
const MAX_DAYS = 3660;
const MAILBOX_STORAGE_KEY = "rfq-foundry.queue.mailboxes";
/** The last area chosen in the queue; the dashboard reads it as its default `?area=`. */
export const AREA_STORAGE_KEY = "i2q.queue.area";
const DAY_MS = 86_400_000;

/** The roles that read the queue without quoting from it (`auth/roles.py`). */
const NON_QUOTING_ROLES = ["manager", "executive"];

/**
 * True when the profile holds an area work role: a sales person who quotes. A manager
 * or an executive reads the queue of a whole area and quotes nothing (invitee review,
 * 12 Oct 2026). A profile without roles (an older backend) counts as a sales person.
 */
export function hasAreaWorkRole(roles: readonly string[] | null | undefined): boolean {
  return !Array.isArray(roles) || !roles.some((role) => NON_QUOTING_ROLES.includes(role));
}

/** "Mine and unassigned" for sales; "Everyone" for a viewer who does not quote. */
export function defaultAssigneeFor(roles: readonly string[] | null | undefined): QueueAssignee {
  return hasAreaWorkRole(roles) ? DEFAULT_ASSIGNEE : "any";
}

/** Colleagues' mails are shown unless the URL says `internal=0`. */
export function showsColleagueMails(params: URLSearchParams): boolean {
  return FEATURES.colleagueForwards
    ? params.get("internal") !== "0"
    : params.get("internal") === "1";
}

/** Replies without lines are folded under their first mail unless the URL says `threads=all`. */
export function groupsThreads(params: URLSearchParams): boolean {
  return FEATURES.groupThreads && params.get("threads") !== "all";
}

const ATTENTION_VALUES: readonly QueueAttention[] = ["back_for_me", "over_1_working_day"];

/** The `attention` param, or undefined for any other value. */
export function toAttention(raw: string | null): Exclude<QueueAttention, "any"> | undefined {
  return ATTENTION_VALUES.includes(raw as QueueAttention)
    ? (raw as Exclude<QueueAttention, "any">)
    : undefined;
}

const CLOSED_FILTER_VALUES: readonly QueueClosedFilter[] = ["no_reply_seen", "today"];

/** The `closed` param of the Closed tab, or undefined for any other value or tab. */
export function toClosedFilter(
  raw: string | null,
  view: QueueView = "closed",
): QueueClosedFilter | undefined {
  return view === "closed" && CLOSED_FILTER_VALUES.includes(raw as QueueClosedFilter)
    ? (raw as QueueClosedFilter)
    : undefined;
}

/** The URL param of the close-offer filter (hunt round 2, decision 8). */
export const CLOSE_OFFER_PARAM = "close_offer";

/**
 * `close_offer=not_an_inquiry`: only the open cases that offer "Close: not an inquiry"
 * (a system notification or a newsletter). Any other value is no filter.
 */
export function closeOfferFilterOf(params: URLSearchParams): "not_an_inquiry" | undefined {
  return params.get(CLOSE_OFFER_PARAM) === "not_an_inquiry" ? "not_an_inquiry" : undefined;
}

export const ASSIGNEE_OPTIONS: readonly { value: QueueAssignee; label: string }[] = [
  { value: "mine_or_unassigned", label: "Mine and unassigned" },
  { value: "me", label: "Mine" },
  { value: "unassigned", label: "Unassigned" },
  { value: "any", label: "Everyone" },
];

const ASSIGNEE_VALUES = ASSIGNEE_OPTIONS.map((option) => option.value);

/**
 * The assignee choices of a viewer: sales get all four; a viewer without an area work
 * role has no "mine and unassigned" (nothing is theirs) and starts on "Everyone".
 */
export function assigneeOptionsFor(
  roles: readonly string[] | null | undefined,
): readonly { value: QueueAssignee; label: string }[] {
  return hasAreaWorkRole(roles)
    ? ASSIGNEE_OPTIONS
    : ASSIGNEE_OPTIONS.filter((option) => option.value !== "mine_or_unassigned");
}

/** The assignee filter of a URL; an unknown value reads as the default. */
export function toAssignee(raw: string | null): QueueAssignee {
  return ASSIGNEE_VALUES.includes(raw as QueueAssignee) ? (raw as QueueAssignee) : DEFAULT_ASSIGNEE;
}

/**
 * The `days` param as a whole number of days back, or null when absent or malformed.
 * 0 is today only ({@link TODAY_DAYS}).
 */
export function toDays(raw: string | null): number | null {
  if (!raw || !/^\d{1,4}$/.test(raw)) {
    return null;
  }
  const days = Number.parseInt(raw, 10);
  return days >= 0 && days <= MAX_DAYS ? days : null;
}

/** `YYYY-MM-DD` of a date in UTC (the queue filters by UTC day). */
export function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` of the viewer's calendar day: what "today" means in the queue. */
export function todayIsoDay(today: Date): string {
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

/**
 * The first day of "the last N days": today for 0, else N days back from today (UTC).
 * Today is the viewer's calendar day, not the UTC day: once the UTC day has rolled over
 * in the afternoon of a viewer west of UTC, the UTC day would drop that morning's mail.
 */
export function daysAgo(days: number, today: Date): string {
  return days === 0 ? todayIsoDay(today) : isoDay(new Date(today.getTime() - days * DAY_MS));
}

/** Lower-cased, de-duplicated mailbox addresses from an untrusted value. */
export function toMailboxList(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry === "string" && entry.trim() !== "") {
      seen.add(entry.trim().toLowerCase());
    }
  }
  return [...seen];
}

/** The remembered mailbox choice of this browser, or [] (also when storage is unavailable). */
export function readRememberedMailboxes(): string[] {
  try {
    const stored = window.localStorage.getItem(MAILBOX_STORAGE_KEY);
    return stored ? toMailboxList(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
}

/** Remember the mailbox choice of this browser. Fails silently without storage. */
export function rememberMailboxes(mailboxes: readonly string[]): void {
  try {
    window.localStorage.setItem(MAILBOX_STORAGE_KEY, JSON.stringify(toMailboxList([...mailboxes])));
  } catch {
    // No storage in this browser: the choice simply lasts for this visit.
  }
}

const AREA_CODE = /^[a-z]{2,4}$/;

/** Lower-cased, de-duplicated area codes from an untrusted value. */
export function toAreaCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  for (const entry of raw) {
    const code = typeof entry === "string" ? entry.trim().toLowerCase() : "";
    if (AREA_CODE.test(code)) {
      seen.add(code);
    }
  }
  return [...seen];
}

/**
 * The area codes last chosen in the queue of this browser, or [] (also when storage is
 * unavailable). The dashboard uses them as its `?area=` when the address names none.
 */
export function readRememberedAreas(): string[] {
  try {
    const stored = window.localStorage.getItem(AREA_STORAGE_KEY);
    return stored ? toAreaCodes(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
}

/** Remember the area choice of this browser. Fails silently without storage. */
export function rememberAreas(codes: readonly string[]): void {
  try {
    window.localStorage.setItem(AREA_STORAGE_KEY, JSON.stringify(toAreaCodes([...codes])));
  } catch {
    // No storage in this browser: the choice simply lasts for this visit.
  }
}

const LAST_QUEUE_KEY = "rfq-foundry.last-queue";

/**
 * The part of a queue URL worth remembering: a reminder filter ("Back for you", "Waiting
 * more than 1 working day") and the page are one-off looks and never come back on the next
 * visit of the Queue link.
 */
export function rememberableSearch(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete("attention");
  params.delete("page");
  return params.toString();
}

/** Keep the queue's current filters so a Queue link or a bare /queue opens them again. */
export function rememberQueueSearch(search: string): void {
  try {
    window.localStorage.setItem(LAST_QUEUE_KEY, rememberableSearch(search));
  } catch {
    // No storage in this browser: the filters last for this page only.
  }
}

/** The last queue view as a query string ("" when none is known). */
export function lastQueueSearch(): string {
  try {
    // Also cleans a value stored before reminder filters were left out.
    return rememberableSearch(window.localStorage.getItem(LAST_QUEUE_KEY) ?? "");
  } catch {
    return "";
  }
}

/** Link target for "Queue": the last view the user had, else the plain queue. */
export function queueHref(): string {
  const search = lastQueueSearch();
  return search ? `/queue?${search}` : "/queue";
}

/** Link target for "Back for you": the cases back for the viewer, any age. */
export const BACK_FOR_YOU_HREF = "/queue?attention=back_for_me";

export interface QueueDefaultsInput {
  /** The mailboxes of the user's scope from `/auth/me`; empty when the server names none. */
  scopeMailboxes: readonly string[];
  /** The remembered choice of this browser. */
  remembered: readonly string[];
  /** The assignee a fresh visit starts with ({@link defaultAssigneeFor}); sales' default when absent. */
  assignee?: QueueAssignee;
}

/** The assignee of a fresh visit for this input. */
function startAssignee(input: QueueDefaultsInput): QueueAssignee {
  return input.assignee ?? DEFAULT_ASSIGNEE;
}

/** The mailboxes a fresh visit starts with: the remembered ones still in scope, else all. */
export function defaultMailboxes({ scopeMailboxes, remembered }: QueueDefaultsInput): string[] {
  const scope = toMailboxList([...scopeMailboxes]);
  if (scope.length > 1) {
    const kept = toMailboxList([...remembered]).filter((mailbox) => scope.includes(mailbox));
    if (kept.length > 0) {
      return kept;
    }
  }
  return scope;
}

/**
 * The search params of a fresh visit with the defaults filled in, or null when the URL
 * already carries the user's own choice (it has an `assignee` param). A "Back for you"
 * link gets no received date: a case back for the viewer can be of any age.
 */
export function withQueueDefaults(
  params: URLSearchParams,
  input: QueueDefaultsInput,
): URLSearchParams | null {
  if (params.has("assignee")) {
    return null;
  }
  const next = new URLSearchParams(params);
  next.set("assignee", startAssignee(input));
  if (!next.has("days") && !next.has("from") && !next.has("to") && !next.has("attention")) {
    next.set("days", String(TODAY_DAYS));
  }
  if (!next.has("mailbox")) {
    defaultMailboxes(input).forEach((mailbox) => next.append("mailbox", mailbox));
  }
  return next;
}

/** True when the filters are exactly the default view (the tab and the page aside). */
export function isDefaultView(params: URLSearchParams, input: QueueDefaultsInput): boolean {
  const sameMailboxes =
    [...toMailboxList(params.getAll("mailbox"))].sort().join("|") ===
    [...defaultMailboxes(input)].sort().join("|");
  return (
    toAssignee(params.get("assignee")) === startAssignee(input) &&
    toDays(params.get("days")) === TODAY_DAYS &&
    !params.has("from") &&
    !params.has("to") &&
    sameMailboxes &&
    !params.has("type") &&
    !params.has("area") &&
    !params.has("sender") &&
    !params.has("q") &&
    !params.has("internal") &&
    !params.has("unmapped") &&
    !params.has("threads") &&
    !params.has("attention") &&
    !params.has(CLOSE_OFFER_PARAM)
  );
}

/**
 * A built-in view of the queue: a tab and a number of days, nothing else. "Today" is the
 * default view itself (0 days back). "Older than 7 days" has no start day and ends 7
 * days ago instead (`olderThanDays`).
 */
export interface QueuePreset {
  id: string;
  label: string;
  view: QueueView;
  days?: number;
  olderThanDays?: number;
}

export const QUEUE_PRESETS: readonly QueuePreset[] = [
  { id: "today", label: "Today", view: "work", days: TODAY_DAYS },
  { id: "week", label: "This week", view: "work", days: 7 },
  ...(FEATURES.backlogChips
    ? [{ id: "older", label: "Older than 7 days", view: "work" as const, olderThanDays: 7 }]
    : []),
];

/**
 * The search params with a built-in view applied: the tab and the dates are set; an
 * explicit date range, a reminder filter and the page are dropped. Every other param
 * (assignee, mailboxes, areas, sender, type, search, toggles) stays exactly as it was,
 * so a view never adds scope.
 */
export function withPreset(
  params: URLSearchParams,
  preset: QueuePreset,
  today: Date = new Date(),
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (preset.view === "work") {
    next.delete("view");
  } else {
    next.set("view", preset.view);
  }
  ["from", "to", "page", "days", "attention"].forEach((key) => next.delete(key));
  if (preset.olderThanDays != null) {
    next.set("to", daysAgo(preset.olderThanDays, today));
  } else {
    next.set("days", String(preset.days ?? 7));
  }
  return next;
}

/** The built-in view the URL matches (same tab, same dates), or null. */
export function activePreset(
  params: URLSearchParams,
  today: Date = new Date(),
): QueuePreset | null {
  if (params.has("attention")) {
    return null;
  }
  const view = params.get("view") ?? "work";
  if (!params.has("from") && !params.has("days") && params.has("to")) {
    return (
      QUEUE_PRESETS.find(
        (preset) =>
          preset.view === view &&
          preset.olderThanDays != null &&
          params.get("to") === daysAgo(preset.olderThanDays, today),
      ) ?? null
    );
  }
  if (params.has("from") || params.has("to")) {
    return null;
  }
  const days = toDays(params.get("days"));
  return (
    QUEUE_PRESETS.find(
      (preset) => preset.view === view && preset.days != null && preset.days === days,
    ) ?? null
  );
}

/**
 * "today" (the default window), "the last 4 days", "7 Oct to 9 Oct" or "any date": the
 * received dates of a URL.
 */
export function rangeText(params: URLSearchParams): string {
  const days = params.has("from") || params.has("to") ? null : toDays(params.get("days"));
  if (days != null) {
    if (days === TODAY_DAYS) {
      return "today";
    }
    return days === 1 ? "the last day" : `the last ${days} days`;
  }
  const from = toIsoDay(params.get("from"));
  const to = toIsoDay(params.get("to"));
  if (from && to) {
    return `${from} to ${to}`;
  }
  if (to) {
    return `received before ${to}`;
  }
  if (from) {
    return `received since ${from}`;
  }
  return "any date";
}

/**
 * The note shown after a built-in view changed the received dates ("Range changed to
 * the last 7 days"), or null when the dates stayed as they were.
 */
export function rangeChangeNote(before: URLSearchParams, after: URLSearchParams): string | null {
  const was = rangeText(before);
  const now = rangeText(after);
  return was === now ? null : `Range changed to ${now} (was ${was})`;
}

/**
 * The "To review" tab over every received date, scope kept: what the backlog chip
 * "<n> open, all dates" counts (`backlog.open_total`), so the list total equals the chip.
 */
export function withAllDates(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  ["view", "days", "from", "to", "page", "attention"].forEach((key) => next.delete(key));
  if (!next.has("assignee")) {
    // Without it the visit would read as fresh and get the default days again.
    next.set("assignee", DEFAULT_ASSIGNEE);
  }
  return next;
}

/** True when the URL is the "To review" tab over every received date. */
export function isAllDates(params: URLSearchParams): boolean {
  return (
    toView(params.get("view")) === "work" &&
    !params.has("days") &&
    !params.has("from") &&
    !params.has("to") &&
    !params.has("attention")
  );
}

/**
 * The text next to Queue in the menu: what is new in the default view and how much is
 * open over all dates ("1 new · 116 open"). Either number can be unknown.
 */
export function queueBadgeText(fresh: number | null, open: number | null): string | null {
  const nf = new Intl.NumberFormat(undefined);
  if (fresh == null && open == null) {
    return null;
  }
  if (open == null) {
    return `${nf.format(fresh ?? 0)} new`;
  }
  if (fresh == null) {
    return `${nf.format(open)} open`;
  }
  return `${nf.format(fresh)} new · ${nf.format(open)} open`;
}

/**
 * The `GET /queue` filters of the default view (tab "To review", mine and unassigned,
 * received today, my default mailboxes, colleagues' mails shown, replies folded). The
 * queue's first request and the sidebar count use these same values, so the count
 * matches what a fresh visit shows. Today is sent as `received_from` alone: nothing is
 * received after now, and a `received_to` of the same UTC day would end before the
 * evening of a viewer west of UTC.
 */
export function defaultQueueFilters(input: QueueDefaultsInput, today: Date): QueueFilters {
  return {
    view: "work",
    assignee: startAssignee(input),
    mailbox: defaultMailboxes(input),
    received_from: daysAgo(TODAY_DAYS, today),
    ...(FEATURES.colleagueForwards ? { include_internal: true } : {}),
    ...(FEATURES.groupThreads ? { group_threads: true } : {}),
  };
}

// ─── the URL as the request of the queue ─────────────────────────────────────

/** Every tab of the queue, in the order of the tab row. */
export const QUEUE_VIEWS: readonly QueueView[] = [
  "work",
  "ready",
  "review",
  "unidentified",
  "replied",
  "waiting-customer",
  "closed",
  "not-rfq",
  "parked",
  "waiting",
  "all",
  "could-not-process",
  "portal",
];

/** The tab of a URL; an unknown value reads as "To review". */
export function toView(raw: string | null): QueueView {
  return QUEUE_VIEWS.includes(raw as QueueView) ? (raw as QueueView) : "work";
}

/** A real `YYYY-MM-DD` day from a search param, or undefined. */
export function toIsoDay(raw: string | null): string | undefined {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return undefined;
  }
  const date = new Date(`${raw}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || isoDay(date) !== raw ? undefined : raw;
}

export const QUEUE_PAGE_SIZE = 50;
export const SEARCH_MAX_LENGTH = 100;
const SENDER_VALUES: readonly string[] = SENDER_OPTIONS.map((option) => option.value);

/**
 * The `GET /queue` filters a queue URL stands for. The queue page asks with them, and the
 * case page asks with them again for "Next case" when the order the queue left runs out.
 */
export function queueFiltersFromParams(params: URLSearchParams, today: Date): QueueFilters {
  const view = toView(params.get("view"));
  const typeOptions = intentOptionsFor(view);
  const intents = params
    .getAll("type")
    .filter((intent) => typeOptions.some((option) => option.value === intent));
  const areaCodes = params.getAll("area").filter((code) => AREA_CODE.test(code));
  const senders = params.getAll("sender").filter((role) => SENDER_VALUES.includes(role));
  const explicitFrom = toIsoDay(params.get("from"));
  const explicitTo = toIsoDay(params.get("to"));
  // "The last N days" applies only without an explicit date range.
  const days = explicitFrom || explicitTo ? null : toDays(params.get("days"));
  const q = (params.get("q") ?? "").trim().slice(0, SEARCH_MAX_LENGTH);
  const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
  return {
    view,
    assignee: toAssignee(params.get("assignee")),
    intent: intents,
    mailbox: params.getAll("mailbox"),
    // Sent only when chosen, so the request of the default view is what it was.
    area: areaCodes.length > 0 ? areaCodes : undefined,
    customer_role: senders.length > 0 ? senders : undefined,
    received_from: days == null ? explicitFrom : daysAgo(days, today),
    received_to: days == null ? explicitTo : undefined,
    q: q || undefined,
    include_internal: showsColleagueMails(params) || undefined,
    include_unmapped: params.get("unmapped") === "1" || undefined,
    group_threads: groupsThreads(params) || undefined,
    attention: toAttention(params.get("attention")),
    closed: toClosedFilter(params.get("closed"), view),
    addressed: params.get("addressed") === "me_or_nobody" ? "me_or_nobody" : undefined,
    // Hunt round 2, decision 8: only the cases that offer "Close: not an inquiry".
    close_offer: closeOfferFilterOf(params),
    limit: QUEUE_PAGE_SIZE,
    offset: (page - 1) * QUEUE_PAGE_SIZE,
  };
}
